import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatIDR, MONTHS_ID } from "../lib/format";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";
import PondokIcon from "../components/PondokIcon";
import { flagFor } from "../lib/countries";

const formatDateShort = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};

const today = () =>
  new Date().toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });

const isIcalBlocked = (b) => {
  if (b?.notes && /imported from|ical/i.test(b.notes)) return true;
  const gn = (b?.guest_name || "").trim().toLowerCase();
  return ["blocked", "closed", "reserved", "not available", "unavailable"].some(
    (k) => gn === k || gn.startsWith(k)
  );
};

export default function BookingsPrint() {
  const [params] = useSearchParams();
  const fStatus = params.get("status") || "All";
  const fPlatform = params.get("platform") || "All";
  const fRoom = params.get("room") || "All";
  const fMonth = params.get("month") || "All";
  const fYear = params.get("year") || String(new Date().getFullYear());
  const fCountry = params.get("country") || "All";

  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/bookings"), api.get("/settings")])
      .then(([b, s]) => {
        let list = (b.data || []).filter((r) => !isIcalBlocked(r));
        if (fStatus !== "All") list = list.filter((r) => r.status === fStatus);
        if (fPlatform !== "All") list = list.filter((r) => r.platform === fPlatform);
        if (fRoom !== "All") list = list.filter((r) => r.room_name === fRoom);
        if (fCountry !== "All") list = list.filter((r) => (r.country || "") === fCountry);
        if (fYear !== "All") list = list.filter((r) => (r.check_in || "").startsWith(`${fYear}-`));
        if (fMonth !== "All") {
          const mm = String(fMonth).padStart(2, "0");
          list = list.filter((r) => {
            const ci = r.check_in || "";
            if (fYear === "All") return ci.slice(5, 7) === mm;
            return ci.startsWith(`${fYear}-${mm}-`);
          });
        }
        // Sort by check_in ASC for printout (chronological)
        list.sort((a, b) => (a.check_in || "").localeCompare(b.check_in || ""));
        setRows(list);
        setSettings(s.data);
      })
      .finally(() => setLoading(false));
  }, [fStatus, fPlatform, fRoom, fMonth, fYear, fCountry]);

  if (loading) return <div className="p-12 text-center text-[#586D61]">Memuat…</div>;

  // Effective deposit: if booking status is Paid → assume fully paid (deposit = total_price)
  const effectiveDeposit = (r) => {
    const total = Number(r.total_price || 0);
    const dep = Number(r.deposit || 0);
    return r.status === "Paid" ? total : Math.min(dep, total);
  };

  // Summary stats
  const totalRevenue = rows.reduce((s, r) => s + Number(r.total_price || 0), 0);
  const totalDeposit = rows.reduce((s, r) => s + effectiveDeposit(r), 0);
  const totalOutstanding = Math.max(0, totalRevenue - totalDeposit);
  const totalGuests = rows.reduce((s, r) => s + Number(r.guests || 0), 0);
  const totalNights = rows.reduce((s, r) => s + Number(r.nights || 0), 0);
  const totalRooms = rows.reduce((s, r) => s + Number(r.rooms_count || 1), 0);

  // Status counts
  const byStatus = {};
  rows.forEach((r) => {
    const k = r.status || "Pending";
    byStatus[k] = (byStatus[k] || 0) + 1;
  });

  // Period label
  let periodLabel = "Semua Periode";
  if (fYear !== "All" && fMonth !== "All") {
    periodLabel = `${MONTHS_ID[Number(fMonth) - 1]} ${fYear}`;
  } else if (fYear !== "All") {
    periodLabel = `Tahun ${fYear}`;
  } else if (fMonth !== "All") {
    periodLabel = MONTHS_ID[Number(fMonth) - 1];
  }

  const filterChips = [];
  if (fStatus !== "All") filterChips.push(`Status: ${fStatus}`);
  if (fPlatform !== "All") filterChips.push(`Platform: ${fPlatform}`);
  if (fRoom !== "All") filterChips.push(`Kamar: ${fRoom}`);
  if (fCountry !== "All") filterChips.push(`Negara: ${fCountry}`);

  return (
    <div className="bg-[#FDFBF7] min-h-screen">
      {/* Toolbar (hidden when printing) */}
      <div className="print:hidden bg-white border-b border-[#EBE6DE] px-6 py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm">
        <Link to="/bookings" className="inline-flex items-center gap-2 text-sm text-[#586D61] hover:text-[#082618]">
          <ArrowLeft className="h-4 w-4" /> Kembali ke Bookings
        </Link>
        <Button
          data-testid="btn-print-bookings"
          onClick={() => window.print()}
          className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl gap-2"
        >
          <Printer className="h-4 w-4" /> Cetak / Save PDF
        </Button>
      </div>

      <div className="max-w-5xl mx-auto p-8 print:p-6">
        {/* Header */}
        <div className="border-b-2 border-[#082618] pb-6 mb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt="Logo" className="h-12 w-12 rounded-xl object-contain bg-white border border-[#EBE6DE]" data-testid="bookings-print-logo" />
              ) : (
                <div className="h-12 w-12 rounded-xl bg-[#D16B4D] flex items-center justify-center">
                  <PondokIcon className="h-7 w-7 text-white" strokeWidth={1.8} />
                </div>
              )}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D16B4D]">PondokRaja</div>
                <div className="font-heading text-2xl font-bold text-[#172E21] leading-tight">
                  {settings?.homestay_name || "Meos Ambower Homestay"}
                </div>
                {settings?.homestay_address && <div className="text-xs text-[#586D61] mt-0.5">{settings.homestay_address}</div>}
              </div>
            </div>
            <div className="text-right text-xs text-[#586D61]">
              <div>Dicetak: {today()}</div>
              <div className="font-mono">{settings?.homestay_email || ""}</div>
              <div className="font-mono">{settings?.homestay_phone || ""}</div>
            </div>
          </div>
          <div className="mt-6 flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Daftar Booking</div>
              <h1 className="font-heading text-3xl font-bold text-[#172E21] mt-1">{periodLabel}</h1>
              {filterChips.length > 0 && (
                <div className="text-xs text-[#586D61] mt-2">Filter: {filterChips.join(" · ")}</div>
              )}
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Total Booking</div>
              <div className="font-mono text-2xl font-bold text-[#172E21]">{rows.length}</div>
              <div className="text-xs text-[#586D61] mt-0.5">{totalGuests} tamu · {totalNights} malam</div>
            </div>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="text-center py-16 text-[#586D61]">
            Tidak ada data booking untuk filter tersebut.
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <SummaryBox label="Pendapatan" value={formatIDR(totalRevenue)} accent="#D16B4D" />
              <SummaryBox label="DP Diterima" value={formatIDR(totalDeposit)} accent="#2C7A51" />
              <SummaryBox label="Sisa Tagihan" value={formatIDR(totalOutstanding)} accent={totalOutstanding > 0 ? "#D99338" : "#2C7A51"} />
              <SummaryBox label="Total Kamar" value={`${totalRooms} kamar`} accent="#586D61" />
            </div>

            {/* Status breakdown */}
            <section className="mb-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61] mb-2">Status Booking</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(byStatus).map(([k, n]) => (
                  <div key={k} className="px-3 py-1.5 rounded-full bg-[#FDFBF7] border border-[#EBE6DE] text-xs">
                    <span className="font-medium text-[#172E21]">{k}:</span>
                    <span className="font-mono ml-1.5 font-bold">{n}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Table */}
            <table className="w-full text-xs border border-[#EBE6DE]">
              <thead className="bg-[#FDFBF7]">
                <tr>
                  <th className="text-left px-2 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE] w-8">#</th>
                  <th className="text-left px-2 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Tamu</th>
                  <th className="text-left px-2 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Kamar</th>
                  <th className="text-left px-2 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Check-in</th>
                  <th className="text-left px-2 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Check-out</th>
                  <th className="text-center px-2 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Mlm</th>
                  <th className="text-center px-2 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Pax</th>
                  <th className="text-left px-2 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Platform</th>
                  <th className="text-left px-2 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Status</th>
                  <th className="text-right px-2 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Total</th>
                  <th className="text-right px-2 py-2 font-bold text-[#172E21] border-b border-[#EBE6DE]">Sisa</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const dep = effectiveDeposit(r);
                  const remaining = Math.max(0, (r.total_price || 0) - dep);
                  return (
                    <tr key={r.id} className="border-b border-[#EBE6DE]/50">
                      <td className="px-2 py-2 text-[#586D61] font-mono">{idx + 1}</td>
                      <td className="px-2 py-2">
                        <div className="font-medium text-[#172E21] flex items-center gap-1">
                          {r.country && <span className="leading-none">{flagFor(r.country)}</span>}
                          <span>{r.guest_name}</span>
                        </div>
                        {r.phone_number && <div className="text-[10px] text-[#586D61] font-mono">{r.phone_number}</div>}
                      </td>
                      <td className="px-2 py-2">
                        {r.room_name}
                        {(r.rooms_count || 1) > 1 && <span className="ml-1 text-[#D16B4D] font-bold">×{r.rooms_count}</span>}
                      </td>
                      <td className="px-2 py-2 font-mono">{formatDateShort(r.check_in)}</td>
                      <td className="px-2 py-2 font-mono">{formatDateShort(r.check_out)}</td>
                      <td className="px-2 py-2 text-center font-mono">{r.nights}</td>
                      <td className="px-2 py-2 text-center font-mono">{r.guests}</td>
                      <td className="px-2 py-2">{r.platform}</td>
                      <td className="px-2 py-2">
                        <span className={`text-[10px] font-bold ${
                          r.status === "Paid" ? "text-[#2C7A51]" :
                          r.status === "Confirmed" ? "text-[#082618]" :
                          r.status === "Cancelled" ? "text-[#D14D4D]" :
                          "text-[#D99338]"
                        }`}>{r.status}</span>
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-bold">{formatIDR(r.total_price)}</td>
                      <td className={`px-2 py-2 text-right font-mono ${remaining > 0 ? "text-[#D99338] font-bold" : "text-[#586D61]"}`}>
                        {remaining > 0 ? formatIDR(remaining) : "Lunas"}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-[#FDFBF7] font-bold border-t-2 border-[#082618]">
                  <td colSpan={5} className="px-2 py-2 text-right">TOTAL</td>
                  <td className="px-2 py-2 text-center font-mono">{totalNights}</td>
                  <td className="px-2 py-2 text-center font-mono">{totalGuests}</td>
                  <td colSpan={2}></td>
                  <td className="px-2 py-2 text-right font-mono">{formatIDR(totalRevenue)}</td>
                  <td className="px-2 py-2 text-right font-mono text-[#D99338]">{formatIDR(totalOutstanding)}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        {/* Footer signature */}
        <div className="mt-16 grid grid-cols-2 gap-12 print:break-inside-avoid">
          <div className="text-center">
            <div className="text-xs text-[#586D61] mb-16">Disiapkan oleh,</div>
            <div className="border-t border-[#082618] pt-2">
              <div className="text-xs font-medium text-[#172E21]">Staff / Admin</div>
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-[#586D61] mb-16">Mengetahui,</div>
            <div className="border-t border-[#082618] pt-2">
              <div className="text-xs font-medium text-[#172E21]">Pemilik Homestay</div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-[10px] text-[#586D61]/60 print:fixed print:bottom-4 print:left-0 print:right-0">
          Generated by PondokRaja · pondokraja.com
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}

function SummaryBox({ label, value, accent }) {
  return (
    <div className="border border-[#EBE6DE] rounded-xl p-3 bg-white">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#586D61]">{label}</div>
      <div className="font-mono text-base font-bold mt-1 truncate" style={{ color: accent }}>{value}</div>
    </div>
  );
}
